import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { PeriodStatus } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const periodCloseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/period-close',
  component: PeriodClosePage,
});

function statusVariant(s: PeriodStatus): 'default' | 'muted' | 'destructive' {
  if (s === 'open') return 'default';
  if (s === 'soft_closed') return 'muted';
  return 'destructive';
}

function statusLabel(s: PeriodStatus): string {
  if (s === 'open') return 'Open';
  if (s === 'soft_closed') return 'Soft-closed';
  return 'Closed';
}

function PeriodClosePage(): JSX.Element {
  const listQuery = useQuery({
    queryKey: ['period-close', 'list'],
    queryFn: () => api.listPeriods(),
  });

  const rows = listQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Period close
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Monthly close checklist, snapshots, and reopen audit trail.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent months</CardTitle>
          <CardDescription>{rows.length} shown</CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              No periods yet. A period row is created the first time you open the checklist for a month.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {rows.map((r) => (
                <li
                  key={r.month}
                  className="flex items-center justify-between gap-3 py-3 text-sm"
                >
                  <div>
                    <div className="font-medium">
                      <Link to="/period-close/$month" params={{ month: r.month }}>
                        {r.month}
                      </Link>
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {r.closedAt ? `Closed ${new Date(r.closedAt).toISOString().slice(0, 10)}` : 'Not closed'}
                      {r.reopenCount > 0 ? ` · reopened ${r.reopenCount}x` : ''}
                    </div>
                  </div>
                  <Badge variant={statusVariant(r.status)}>{statusLabel(r.status)}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
